import { DefaultTheme } from 'styled-components'

import { AlertBannerType } from '~/components/AlertBanner/AlertBanner.type'

import {
  AreaLineColorSchemeType,
  AreaLineStyleType
} from '~/components/AreaLine/type'

import { AvatarStyleType } from '~/components/Avatar/Avatar.type'
import { BreadcrumbStyleType } from '~/components/Breadcrumbs/type'
import { ExplicitSelectionButtonStyleType } from '~/components/Buttons/ExplicitSelectionButton/type'
import { CardStyleType } from '~/components/Cards/DocumentListCard/type'
import { DatePickerStyleType } from '~/components/DataFields/DatePicker/type'
import { InputStyleType } from '~/components/DataFields/Input/type'
import { OneTimePasswordType } from '~/components/DataFields/OneTimePassword/OneTimePassword.type'
import { DividerType } from '~/components/Divider/Divider.type'
import { DotNavigationType } from '~/components/DotNavigation/DotNavigation.type'
import { EvaluationStyleType } from '~/components/Evaluation/Evaluation.type'
import { FeedbackCardType } from '~/components/FeedbackCard/type'
import { IconStyleType } from '~/components/Icon/type'
import { LabelStyleType } from '~/components/Label/type'
import { NavigationSliderType } from '~/components/NavigationSlider/type'
import { PopupStyleType } from '~/components/Popup/Popup.type'
import { PriorityTagStyleType } from '~/components/PriorityTag/PriorityTag.type'
import { ProgressBarStyleType } from '~/components/ProgressBar/ProgressBar.types'
import { SpinnerStyleType } from '~/components/Spinner/type'
import { StarRatingStyleType } from '~/components/StarRating/type'

import { StatusClass } from '~/utils/enum/classes'

import ColorPalette, {
  ColorPaletteAlertBanner,
  ColorPaletteCES,
  ColorPaletteFeedbackCard,
  ColorPaletteMouseOver
} from '../colorPalette'

import saf from './default/saf'
import { MouseOverType } from '~/components/MouseOver/MouseOver.types'

const statusColors: Record<StatusClass, string> = {
  default: '#ABBED1',
  processing: '#FFDE69',
  error: '#FF7373',
  valid: '#74EC81'
}

const areaLineColorSchemes: AreaLineColorSchemeType = {
  black: {
    background: '#0C1014',
    text: '#F4F4F4'
  },
  white: {
    background: '#FFF',
    text: '#303032'
  },
  blue: {
    background: '#006BFF',
    text: '#FFF'
  },
  orange: {
    background: '#C25400',
    text: '#FFF'
  },
  green: {
    background: '#2C6C2F',
    text: '#FFF'
  },
  red: {
    background: '#9B0000',
    text: '#FFF'
  },
  gray: {
    background: '#727272',
    text: '#FFF'
  }
}

const colors = {
  primary: '#006BFF',
  defaultBackground: ColorPalette.white,
  status: statusColors,
  ...ColorPalette,
  ...ColorPaletteCES
}

const fonts = {
  barlow: "'Barlow', sans-serif"
}

const grid = {
  columnWidth: 128,
  gutter: 24,
  margin: 48,
  additionalMargin: 12
}

const button = {
  focusBorderColor: ColorPalette.focusBorderColor,
  clickedBackground: ColorPalette._C7DEFF,
  fontWeight: 700,
  padding: {
    extraSmall: '2px 2px',
    small: '3px 2px',
    semiSmall: '6.5px 2.5px',
    regular: '9px 3px',
    semiLarge: '10px 4px',
    large: '18px 4px',
    extraLarge: '46px 6px',
    semiGiant: '96px 8px'
  },
  fontSizes: {
    extraSmall: '8px',
    small: '10px',
    semiSmall: '10px',
    regular: '12px',
    semiLarge: '12px',
    large: '16px',
    extraLarge: '20px',
    semiGiant: '32px'
  },
  lineHeights: {
    extraSmall: '1.5',
    small: '1.4',
    semiSmall: '1.4',
    regular: '1.3',
    semiLarge: '1.3',
    large: '1.25',
    extraLarge: '1.2',
    semiGiant: '1.5'
  },
  heights: {
    extraSmall: '20px',
    small: '24px',
    semiSmall: '32px',
    regular: '40px',
    semiLarge: '48px',
    large: '64px',
    extraLarge: '128px',
    semiGiant: '256px'
  },
  border: {
    extraSmall: '2px',
    small: '2px',
    semiSmall: '2.5px',
    regular: '3px',
    semiLarge: '4px',
    large: '4px',
    extraLarge: '6px',
    semiGiant: '8px'
  },
  borderRadius: {
    extraSmall: '4px',
    small: '4px',
    semiSmall: '4px',
    regular: '4px',
    semiLarge: '8px',
    large: '8px',
    extraLarge: '8px',
    semiGiant: '16px'
  },
  dropdownWidths: {
    extraSmall: '16px',
    small: '20px',
    semiSmall: '26px',
    regular: '32px',
    semiLarge: '44px',
    large: '56px',
    extraLarge: '64px',
    semiGiant: '96px'
  },
  textColors: {
    black: {
      color: ColorPalette.blackText,
      impactClasses: {
        statusChange: ColorPalette.statusChangeBlackText,
        situationChange: ColorPalette.situationChangeBlackText,
        settings: ColorPalette.settingsBlackText,
        workflow: ColorPalette.workflowBlackText,
        navigation: ColorPalette.navigationBlackText
      }
    },
    white: {
      color: ColorPalette.whiteText,
      impactClasses: {
        statusChange: ColorPalette.statusChangeWhiteText,
        situationChange: ColorPalette.situationChangeWhiteText,
        settings: ColorPalette.settingsWhiteText,
        workflow: ColorPalette.workflowWhiteText,
        navigation: ColorPalette.navigationWhiteText
      }
    }
  }
}

const icon: IconStyleType = {
  sizes: {
    extraSmall: 12,
    small: 14,
    semiSmall: 16,
    regular: 20,
    semiLarge: 24,
    large: 32,
    extraLarge: 48,
    semiGiant: 56,
    giant: 72,
    extraGiant: 96
  }
}

const label: LabelStyleType = {
  large: {
    size: 16,
    weight: 500
  },
  regular: {
    size: 14,
    weight: 500
  },
  small: {
    size: 12,
    weight: 500
  }
}

const navigationSlider: NavigationSliderType = {
  colorSchemes: {
    level_1: ColorPalette.level_1,
    level_2: ColorPalette.level_2,
    level_3: ColorPalette.level_3
  },
  sizes: {
    extraSmall: {
      totalHeight: 40,
      circleSize: 30,
      verticalPadding: 7,
      elementsSpacing: 4,
      currentPageTextTypography: 'content-04',
      currentPageTypography: 'content-emphasis-04',
      totalPageTextTypography: 'content-04',
      totalPagesTypography: 'content-emphasis-04',
      componentPaddingHorizontal: 8,
      editableValueWidth: 20,
      editableValueBorder: 1.5
    },
    small: {
      totalHeight: 48,
      circleSize: 40,
      verticalPadding: 6,
      elementsSpacing: 4,
      currentPageTextTypography: 'content-04',
      currentPageTypography: 'content-emphasis-04',
      totalPageTextTypography: 'content-04',
      totalPagesTypography: 'content-emphasis-04',
      componentPaddingHorizontal: 8,
      editableValueWidth: 20,
      editableValueBorder: 2
    },
    semiSmall: {
      totalHeight: 56,
      circleSize: 40,
      verticalPadding: 10,
      elementsSpacing: 4,
      currentPageTextTypography: 'content-04',
      currentPageTypography: 'content-emphasis-04',
      totalPageTextTypography: 'content-04',
      totalPagesTypography: 'content-emphasis-04',
      componentPaddingHorizontal: 12,
      editableValueWidth: 24,
      editableValueBorder: 2
    },
    regular: {
      totalHeight: 64,
      circleSize: 60,
      verticalPadding: 6,
      elementsSpacing: 4,
      currentPageTextTypography: 'content-05',
      currentPageTypography: 'content-emphasis-05',
      totalPageTextTypography: 'content-05',
      totalPagesTypography: 'content-emphasis-05',
      componentPaddingHorizontal: 12,
      editableValueWidth: 38,
      editableValueBorder: 2.5
    },
    semiLarge: {
      totalHeight: 72,
      circleSize: 60,
      verticalPadding: 9,
      elementsSpacing: 6,
      currentPageTextTypography: 'content-05',
      currentPageTypography: 'content-emphasis-05',
      totalPageTextTypography: 'content-05',
      totalPagesTypography: 'content-emphasis-05',
      componentPaddingHorizontal: 12,
      editableValueWidth: 56,
      editableValueBorder: 2.5
    },
    large: {
      totalHeight: 80,
      circleSize: 70,
      verticalPadding: 9,
      elementsSpacing: 6,
      currentPageTextTypography: 'content-06',
      currentPageTypography: 'content-emphasis-06',
      totalPageTextTypography: 'content-06',
      totalPagesTypography: 'content-emphasis-06',
      componentPaddingHorizontal: 12,
      editableValueWidth: 64,
      editableValueBorder: 3
    },
    extraLarge: {
      totalHeight: 88,
      circleSize: 90,
      verticalPadding: 5,
      elementsSpacing: 8,
      currentPageTextTypography: 'content-07',
      currentPageTypography: 'content-emphasis-07',
      totalPageTextTypography: 'content-07',
      totalPagesTypography: 'content-emphasis-07',
      componentPaddingHorizontal: 12,
      editableValueWidth: 72,
      editableValueBorder: 3
    },
    semiGiant: {
      totalHeight: 112,
      circleSize: 120,
      verticalPadding: 4,
      elementsSpacing: 8,
      currentPageTextTypography: 'content-08',
      currentPageTypography: 'content-emphasis-08',
      totalPageTextTypography: 'content-08',
      totalPagesTypography: 'content-emphasis-08',
      componentPaddingHorizontal: 14,
      editableValueWidth: 80,
      editableValueBorder: 3.5
    }
  }
}

const explicitSelectionButton: ExplicitSelectionButtonStyleType = {
  extraSmall: {
    generalLabelPadding: 8,
    borderSize: 1,
    width: 20,
    height: 20,
    spaceBetween: 4,
    innerCirclePadding: 4
  },
  small: {
    generalLabelPadding: 4,
    borderSize: 1,
    width: 24,
    height: 24,
    spaceBetween: 6,
    innerCirclePadding: 4
  },
  semiSmall: {
    generalLabelPadding: 8,
    borderSize: 2,
    width: 32,
    height: 32,
    spaceBetween: 6,
    innerCirclePadding: 4
  },
  regular: {
    generalLabelPadding: 8,
    borderSize: 2,
    width: 40,
    height: 40,
    spaceBetween: 6,
    innerCirclePadding: 4
  },
  semiLarge: {
    generalLabelPadding: 8,
    borderSize: 3,
    width: 48,
    height: 48,
    spaceBetween: 8,
    innerCirclePadding: 4
  },
  large: {
    generalLabelPadding: 12,
    borderSize: 3,
    width: 64,
    height: 64,
    spaceBetween: 12,
    innerCirclePadding: 4
  },
  extraLarge: {
    generalLabelPadding: 8,
    borderSize: 6,
    width: 128,
    height: 128,
    spaceBetween: 16,
    innerCirclePadding: 4
  },
  semiGiant: {
    generalLabelPadding: 8,
    borderSize: 10,
    width: 256,
    height: 256,
    spaceBetween: 32,
    innerCirclePadding: 4
  }
}

const informationCard = {
  extraSmall: {
    headerCardHeight: 32,
    InformationButtonSize: 24,
    titleSize: 24,
    headerSpacingBottom: 20,
    horizontalSpacing: 8,
    verticalSpacing: 8,
    titleRightSpacing: 4
  },
  small: {
    headerCardHeight: 40,
    InformationButtonSize: 32,
    titleSize: 32,
    headerSpacingBottom: 20,
    horizontalSpacing: 8,
    verticalSpacing: 8,
    titleRightSpacing: 4
  },
  semiSmall: {
    headerCardHeight: 48,
    InformationButtonSize: 40,
    titleSize: 40,
    headerSpacingBottom: 20,
    horizontalSpacing: 8,
    verticalSpacing: 8,
    titleRightSpacing: 4
  },
  regular: {
    headerCardHeight: 56,
    InformationButtonSize: 48,
    titleSize: 48,
    headerSpacingBottom: 20,
    horizontalSpacing: 8,
    verticalSpacing: 8,
    titleRightSpacing: 8
  },
  semiLarge: {
    headerCardHeight: 72,
    InformationButtonSize: 64,
    titleSize: 64,
    headerSpacingBottom: 24,
    horizontalSpacing: 8,
    verticalSpacing: 8,
    titleRightSpacing: 8
  },
  large: {
    headerCardHeight: 72,
    InformationButtonSize: 64,
    titleSize: 64,
    headerSpacingBottom: 24,
    horizontalSpacing: 8,
    verticalSpacing: 8,
    titleRightSpacing: 8
  },
  extraLarge: {
    headerCardHeight: 136,
    InformationButtonSize: 128,
    titleSize: 128,
    headerSpacingBottom: 24,
    horizontalSpacing: 8,
    verticalSpacing: 8,
    titleRightSpacing: 16
  },
  semiGiant: {
    headerCardHeight: 264,
    InformationButtonSize: 256,
    titleSize: 256,
    headerSpacingBottom: 24,
    horizontalSpacing: 8,
    verticalSpacing: 8,
    titleRightSpacing: 32
  }
}

const input: InputStyleType = {
  extraSmall: {
    fontSize: 8,
    labelSize: 8,
    lineHeight: 12,
    labelLineHeight: 12,
    labelAreaMargin: 8,
    labelSpaceBetween: 4,
    paddingX: 4,
    paddingY: 2,
    boxHeight: '20px',
    borderRadius: 4
  },
  small: {
    fontSize: 10,
    labelSize: 14,
    lineHeight: 14,
    labelLineHeight: 20,
    labelAreaMargin: 8,
    labelSpaceBetween: 4,
    paddingX: 6,
    paddingY: 4,
    boxHeight: '24px',
    borderRadius: 4
  },
  semiSmall: {
    fontSize: 12,
    labelSize: 14,
    lineHeight: 16,
    labelLineHeight: 20,
    labelAreaMargin: 6,
    labelSpaceBetween: 4,
    paddingX: 6,
    paddingY: 4,
    boxHeight: '32px',
    borderRadius: 4
  },
  regular: {
    fontSize: 14,
    labelSize: 16,
    lineHeight: 20,
    labelLineHeight: 24,
    labelAreaMargin: 4,
    labelSpaceBetween: 4,
    paddingX: 10,
    paddingY: 8,
    boxHeight: '40px',
    borderRadius: 4
  },
  semiLarge: {
    fontSize: 14,
    labelSize: 18,
    lineHeight: 20,
    labelLineHeight: 24,
    labelAreaMargin: 4,
    labelSpaceBetween: 8,
    paddingX: 10,
    paddingY: 8,
    boxHeight: '48px',
    borderRadius: 8
  },
  large: {
    fontSize: 16,
    labelSize: 18,
    lineHeight: 24,
    labelLineHeight: 24,
    labelAreaMargin: 4,
    labelSpaceBetween: 16,
    paddingX: 10,
    paddingY: 8,
    boxHeight: '64px',
    borderRadius: 8
  },
  extraLarge: {
    fontSize: 18,
    labelSize: 24,
    lineHeight: 24,
    labelLineHeight: 32,
    labelAreaMargin: 4,
    labelSpaceBetween: 16,
    paddingX: 10,
    paddingY: 8,
    boxHeight: '128px',
    borderRadius: 8
  },
  semiGiant: {
    fontSize: 18,
    labelSize: 24,
    lineHeight: 24,
    labelLineHeight: 32,
    labelAreaMargin: 4,
    labelSpaceBetween: 32,
    paddingX: 10,
    paddingY: 8,
    boxHeight: '256px',
    borderRadius: 16
  }
}

const spinner: SpinnerStyleType = {
  sizes: {
    extraSmall: 8,
    small: 10,
    semiSmall: 14,
    regular: 16,
    semiLarge: 16,
    large: 20,
    extraLarge: 20,
    semiGiant: 20
  }
}

const datepicker: DatePickerStyleType = {
  extraSmall: {
    dayLineHeight: 20,
    dayFontSize: 12,
    iconSize: 8
  },
  small: {
    dayLineHeight: 30,
    dayFontSize: 14,
    iconSize: 10
  },
  semiSmall: {
    dayLineHeight: 30,
    dayFontSize: 14,
    iconSize: 14
  },
  regular: {
    dayLineHeight: 30,
    dayFontSize: 14,
    iconSize: 16
  },
  semiLarge: {
    dayLineHeight: 30,
    dayFontSize: 16,
    iconSize: 16
  },
  large: {
    dayLineHeight: 40,
    dayFontSize: 16,
    iconSize: 20
  },
  extraLarge: {
    dayLineHeight: 40,
    dayFontSize: 16,
    iconSize: 20
  },
  semiGiant: {
    dayLineHeight: 40,
    dayFontSize: 16,
    iconSize: 20
  }
}

const card: CardStyleType = {
  extraSmall: {
    shadowSize: '0 3px 6px',
    padding: 8
  },
  small: {
    shadowSize: '0 3px 6px',
    padding: 10
  },
  semiSmall: {
    shadowSize: '0 3px 6px',
    padding: 12
  },
  regular: {
    shadowSize: '0 3px 8px',
    padding: 16
  },
  semiLarge: {
    shadowSize: '0 3px 8px',
    padding: 20
  },
  large: {
    shadowSize: '0 3px 8px',
    padding: 24
  },
  extraLarge: {
    shadowSize: '0 3px 8px',
    padding: 32
  },
  semiGiant: {
    shadowSize: '0 5px 12px',
    padding: 64
  }
}

const starRating: StarRatingStyleType = {
  extraSmall: {
    iconSize: 'small',
    desFontSize: 12,
    complementFontSize: 6,
    vSpacing: 0,
    starsMargin: 0
  },
  small: {
    iconSize: 'regular',
    desFontSize: 16,
    complementFontSize: 8,
    vSpacing: 2,
    starsMargin: 2
  },
  semiSmall: {
    iconSize: 'large',
    desFontSize: 20,
    complementFontSize: 12,
    vSpacing: 2,
    starsMargin: 4
  },
  regular: {
    iconSize: 'large',
    desFontSize: 24,
    complementFontSize: 16,
    vSpacing: 4,
    starsMargin: 8
  },
  semiLarge: {
    iconSize: 'extraLarge',
    desFontSize: 32,
    complementFontSize: 16,
    vSpacing: 4,
    starsMargin: 8
  },
  large: {
    iconSize: 'semiGiant',
    desFontSize: 48,
    complementFontSize: 26,
    vSpacing: 4,
    starsMargin: 8
  },
  extraLarge: {
    iconSize: 'semiGiant',
    desFontSize: 64,
    complementFontSize: 30,
    vSpacing: 4,
    starsMargin: 8
  },
  semiGiant: {
    iconSize: 'semiGiant',
    desFontSize: 64,
    complementFontSize: 30,
    vSpacing: 4,
    starsMargin: 8
  }
}

const popup: PopupStyleType = {
  extraSmall: {
    padding: '2rem'
  },
  small: {
    padding: '2rem'
  },
  semiSmall: {
    padding: '2rem'
  },
  regular: {
    padding: '2.5rem'
  },
  semiLarge: {
    padding: '2.5rem'
  },
  large: {
    padding: '2.5rem'
  },
  extraLarge: {
    padding: '2.5rem'
  },
  semiGiant: {
    padding: '2.5rem'
  }
}

const feedbackCard: FeedbackCardType = {
  type: {
    default: ColorPaletteFeedbackCard.default,
    success: ColorPaletteFeedbackCard.success,
    warning: ColorPaletteFeedbackCard.warning,
    error: ColorPaletteFeedbackCard.error
  },
  sizes: {
    extraSmall: {
      padding: 4,
      titleTextClass: 'content-emphasis-01',
      descriptionTextClass: 'content-01'
    },
    small: {
      padding: 6,
      titleTextClass: 'content-emphasis-02',
      descriptionTextClass: 'content-01'
    },
    semiSmall: {
      padding: 8,
      titleTextClass: 'content-emphasis-02',
      descriptionTextClass: 'content-02'
    },
    regular: {
      padding: 10,
      titleTextClass: 'content-emphasis-03',
      descriptionTextClass: 'content-02'
    },
    semiLarge: {
      padding: 12,
      titleTextClass: 'content-emphasis-04',
      descriptionTextClass: 'content-02'
    },
    large: {
      padding: 14,
      titleTextClass: 'content-emphasis-04',
      descriptionTextClass: 'content-03'
    },
    extraLarge: {
      padding: 16,
      titleTextClass: 'content-emphasis-05',
      descriptionTextClass: 'content-04'
    },
    semiGiant: {
      padding: 20,
      titleTextClass: 'content-emphasis-06',
      descriptionTextClass: 'content-05'
    }
  }
}

const breadcrumb: BreadcrumbStyleType = {
  sizes: {
    extraSmall: {
      size: '14px',
      lineHeight: '20px',
      spaceBetween: '16px'
    },
    small: {
      size: '16px',
      lineHeight: '24px',
      spaceBetween: '16px'
    },
    semiSmall: {
      size: '16px',
      lineHeight: '24px',
      spaceBetween: '16px'
    },
    regular: {
      size: '16px',
      lineHeight: '24px',
      spaceBetween: '16px'
    },
    semiLarge: {
      size: '16px',
      lineHeight: '24px',
      spaceBetween: '24px'
    },
    large: {
      size: '18px',
      lineHeight: '24px',
      spaceBetween: '24px'
    },
    extraLarge: {
      size: '24px',
      lineHeight: '32px',
      spaceBetween: '24px'
    },
    semiGiant: {
      size: '32px',
      lineHeight: '48px',
      spaceBetween: '32px'
    }
  },
  colors: {
    white: '#FFF',
    black: '#303032'
  }
}

const areaLine: AreaLineStyleType = {
  extraSmall: {
    height: '40px',
    horizontalPadding: '39px',
    spaceBetween: '16px',
    verticalPadding: '11px'
  },
  small: {
    height: '48px',
    horizontalPadding: '39px',
    spaceBetween: '16px',
    verticalPadding: '12px'
  },
  semiSmall: {
    height: '56px',
    horizontalPadding: '39px',
    spaceBetween: '16px',
    verticalPadding: '17px'
  },
  regular: {
    height: '64px',
    horizontalPadding: '39px',
    spaceBetween: '16px',
    verticalPadding: '18px'
  },
  semiLarge: {
    height: '72px',
    horizontalPadding: '39px',
    spaceBetween: '24px',
    verticalPadding: '22px'
  },
  large: {
    height: '80px',
    horizontalPadding: '39px',
    spaceBetween: '24px',
    verticalPadding: '22px'
  },
  extraLarge: {
    height: '88px',
    horizontalPadding: '39px',
    spaceBetween: '24px',
    verticalPadding: '17px'
  },
  semiGiant: {
    height: '96px',
    horizontalPadding: '39px',
    spaceBetween: '32px',
    verticalPadding: '21px'
  }
}

const priorityTag: PriorityTagStyleType = {
  extraSmall: {
    textClass: 'content-emphasis-01',
    padding: 1
  },
  small: {
    textClass: 'content-emphasis-02',
    padding: 2
  },
  semiSmall: {
    textClass: 'content-emphasis-03',
    padding: 3
  },
  regular: {
    textClass: 'content-emphasis-04',
    padding: 4
  },
  semiLarge: {
    textClass: 'content-emphasis-05',
    padding: 5
  },
  large: {
    textClass: 'content-emphasis-06',
    padding: 6
  },
  extraLarge: {
    textClass: 'content-emphasis-07',
    padding: 7
  },
  semiGiant: {
    textClass: 'content-emphasis-08',
    padding: 8
  }
}

const avatar: AvatarStyleType = {
  extraSmall: {
    size: 20,
    spaceBetweenElements: 4
  },
  small: {
    size: 24,
    spaceBetweenElements: 4
  },
  semiSmall: {
    size: 32,
    spaceBetweenElements: 4
  },
  regular: {
    size: 40,
    spaceBetweenElements: 4
  },
  semiLarge: {
    size: 48,
    spaceBetweenElements: 6
  },
  large: {
    size: 64,
    spaceBetweenElements: 6
  },
  extraLarge: {
    size: 128,
    spaceBetweenElements: 8
  },
  semiGiant: {
    size: 256,
    spaceBetweenElements: 16
  }
}

const evaluation: EvaluationStyleType = {
  extraSmall: {
    defaultSpace: 8,
    containerPadding: 8
  },
  small: {
    defaultSpace: 8,
    containerPadding: 8
  },
  semiSmall: {
    defaultSpace: 8,
    containerPadding: 8
  },
  regular: {
    defaultSpace: 8,
    containerPadding: 8
  },
  semiLarge: {
    defaultSpace: 16,
    containerPadding: 16
  },
  large: {
    defaultSpace: 16,
    containerPadding: 16
  },
  extraLarge: {
    defaultSpace: 16,
    containerPadding: 16
  },
  semiGiant: {
    defaultSpace: 16,
    containerPadding: 32
  }
}

const oneTimePassword: OneTimePasswordType = {
  extraSmall: {
    spaceBetween: 16,
    size: 20
  },
  small: {
    spaceBetween: 16,
    size: 24
  },
  semiSmall: {
    spaceBetween: 24,
    size: 32
  },
  regular: {
    spaceBetween: 24,
    size: 40
  },
  semiLarge: {
    spaceBetween: 32,
    size: 48
  },
  large: {
    spaceBetween: 32,
    size: 64
  },
  extraLarge: {
    spaceBetween: 64,
    size: 128
  },
  semiGiant: {
    spaceBetween: 64,
    size: 256
  }
}

const progressBar: ProgressBarStyleType = {
  /* Common styles */
  loadingColor: statusColors.processing,
  loadedColor: statusColors.valid,
  errorColor: statusColors.error,
  backgroundColor: ColorPalette.colorSupportLightGrey,

  extraSmall: {
    textClass: 'content-emphasis-01',
    verticalPadding: 4,
    totalHeight: 20
  },
  small: {
    textClass: 'content-emphasis-02',
    verticalPadding: 5,
    totalHeight: 24
  },
  semiSmall: {
    textClass: 'content-emphasis-02',
    verticalPadding: 9,
    totalHeight: 32
  },
  regular: {
    textClass: 'content-emphasis-03',
    verticalPadding: 12,
    totalHeight: 40
  },
  semiLarge: {
    textClass: 'content-emphasis-03',
    verticalPadding: 16,
    totalHeight: 48
  },
  large: {
    textClass: 'content-emphasis-04',
    verticalPadding: 22,
    totalHeight: 64
  },
  extraLarge: {
    textClass: 'content-emphasis-05',
    verticalPadding: 52,
    totalHeight: 128
  },
  semiGiant: {
    textClass: 'content-emphasis-07',
    verticalPadding: 104,
    totalHeight: 256
  }
}

const dotNavigation: DotNavigationType = {
  mobile: {
    containerSize: 20,
    dotSize: {
      small: 10,
      regular: 14,
      large: 18
    },
    spaceBetween: 35
  },
  desktop: {
    containerSize: 24,
    dotSize: {
      small: 12,
      regular: 16,
      large: 20
    },
    spaceBetween: 48
  }
}

const alertBanner: AlertBannerType = {
  statusColorPallete: {
    default: ColorPaletteAlertBanner.default,
    success: ColorPaletteAlertBanner.success,
    warning: ColorPaletteAlertBanner.warning,
    error: ColorPaletteAlertBanner.error
  }
}

const divider: DividerType = {
  height: {
    extraSmall: 1,
    small: 2,
    semiSmall: 3,
    regular: 4
  },
  spacing: {
    extraSmall: {
      margin: 4
    },
    small: {
      margin: 8
    },
    semiSmall: {
      margin: 10
    },
    regular: {
      margin: 12
    },
    semiLarge: {
      margin: 14
    },
    large: {
      margin: 16
    },
    extraLarge: {
      margin: 20
    },
    semiGiant: {
      margin: 24
    }
  }
}

const mouseOver: MouseOverType = {
  type: {
    default: ColorPaletteMouseOver.default,
    success: ColorPaletteMouseOver.success,
    warning: ColorPaletteMouseOver.warning,
    error: ColorPaletteMouseOver.error
  },
  sizes: {
    extraSmall: {
      padding: 4,
      titleTextClass: 'content-emphasis-03',
      descriptionTextClass: 'content-03'
    },
    small: {
      padding: 6,
      titleTextClass: 'content-emphasis-04',
      descriptionTextClass: 'content-03'
    },
    semiSmall: {
      padding: 8,
      titleTextClass: 'content-emphasis-04',
      descriptionTextClass: 'content-04'
    },
    regular: {
      padding: 10,
      titleTextClass: 'content-emphasis-05',
      descriptionTextClass: 'content-04'
    },
    semiLarge: {
      padding: 12,
      titleTextClass: 'content-emphasis-05',
      descriptionTextClass: 'content-05'
    },
    large: {
      padding: 14,
      titleTextClass: 'content-emphasis-06',
      descriptionTextClass: 'content-05'
    },
    extraLarge: {
      padding: 16,
      titleTextClass: 'content-emphasis-07',
      descriptionTextClass: 'content-06'
    },
    semiGiant: {
      padding: 20,
      titleTextClass: 'content-emphasis-08',
      descriptionTextClass: 'content-07'
    }
  }
}

const defaultTheme: DefaultTheme = {
  colors,
  areaLineColorSchemes,
  fonts,
  fontSize: ['36px'],
  grid,
  button,
  icon,
  label,
  navigationSlider,
  explicitSelectionButton,
  informationCard,
  input,
  spinner,
  datepicker,
  card,
  feedbackCard,
  breadcrumb,
  areaLine,
  starRating,
  popup,
  priorityTag,
  avatar,
  evaluation,
  oneTimePassword,
  progressBar,
  dotNavigation,
  saf,
  alertBanner,
  divider,
  mouseOver
}

export default defaultTheme
